import {
  Directive,
  Input,
  ViewContainerRef,
  Type,
  TemplateRef,
  OnDestroy,
  Inject,
} from '@angular/core';
import { TranslateParser } from '@ngx-translate/core';
import { Token, TokenArray } from '@cigna/resource-content/contracts';
import { Renderer, RENDER_CONFIG, RenderConfig } from './renderer.interface';
import { LinkRendererComponent } from './link-renderer.component';
import {
  BoldRendererComponent,
  StrongRendererComponent,
  BreakRendererComponent,
  ItalicRendererComponent,
  EmRendererComponent,
  ParagraphRendererComponent,
  SubRendererComponent,
  SupRendererComponent,
  SpanRendererComponent,
} from './formatting-renderers';
import {
  ButtonRendererComponent,
  IconRendererComponent,
} from './ui-component-renderers';
import { mapToken } from '../content/content-mapper';

const RENDERER_MAP: { [key: string]: Type<Renderer> } = {
  a: LinkRendererComponent,
  b: BoldRendererComponent,
  strong: StrongRendererComponent,
  br: BreakRendererComponent,
  i: ItalicRendererComponent,
  em: EmRendererComponent,
  p: ParagraphRendererComponent,
  sub: SubRendererComponent,
  sup: SupRendererComponent,
  span: SpanRendererComponent,
  'cigna-button': ButtonRendererComponent,
  'cigna-icon': IconRendererComponent,
};

@Directive({
  selector: '[cignaContentRenderer]',
})
export class ContentRendererDirective implements OnDestroy {
  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _templateRef: TemplateRef<any>,
    private _translateParser: TranslateParser,
    @Inject(RENDER_CONFIG) private _config: RenderConfig,
  ) {}

  @Input() set cignaContentRenderer(token: Token | null) {
    this._viewContainerRef.clear();

    this.addToken(token, this._config());
  }

  ngOnDestroy() {
    this._viewContainerRef.clear();
  }

  private addToken(
    token: Token | null,
    config: ReturnType<RenderConfig>,
    isTokenArray = true,
  ) {
    if (!token) {
      return;
    }

    if (typeof token === 'string') {
      this._viewContainerRef.createEmbeddedView(this._templateRef, {
        $implicit: this._translateParser.interpolate(token, config.params),
      });

      return;
    }

    if (isTokenArray) {
      (token as TokenArray).forEach((child) =>
        this.addToken(child, config, false),
      );

      return;
    }

    const customTemplate = config.templates[token[0]];

    if (customTemplate && customTemplate.length) {
      this._viewContainerRef.createEmbeddedView(customTemplate[0], {
        $implicit: mapToken(token, (raw) =>
          this._translateParser.interpolate(raw, config.params),
        ),
      });

      return;
    }

    const renderer = RENDERER_MAP[token[0]];

    if (!renderer) {
      return;
    }

    const component =
      this._viewContainerRef.createComponent<Renderer>(renderer);
    component.instance.token = token;
  }
}
